import React, { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { StratEduLogo } from '../../components/brand/StratEduLogo';
import { Button } from '../../components/common/Button';
import { Card, CardHeader, CardContent } from '../../components/common/Card';
import { Input } from '../../components/forms/Input';
import { loginSchema, LoginFormData } from '../../schemas/authSchemas';
import { fetchUserPermissions } from '../../services/permissionsService';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loginUser , setUserPermissions } from '../../store/slices/authSlice';

interface LoginLocationState {
  from?: {
    pathname?: string;
  };
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isLoading, isAuthenticated } = useAppSelector(state => state.auth);

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    mode: 'all',
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from =
        (location.state as LoginLocationState | null)?.from?.pathname ||
        '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const onSubmit = async (
    data: LoginFormData,
    event?: React.BaseSyntheticEvent
  ) => {
    if (event) {
      event.preventDefault();
    }
    try {
      const loginRes = await dispatch(
        loginUser({
          email: data.email,
          password: data.password,
        })
      ).unwrap();

      // Fetch user permissions after successful login and save in user slice
      try {
        const perms = await fetchUserPermissions(loginRes.access_token);
        dispatch(
          setUserPermissions({
            permissions: perms.permissions,
            accessible_districts: perms.accessible_districts,
            accessible_schools: perms.accessible_schools,
          })
        );
      } catch {}

      // Show success toast
      toast.success('Welcome back!');
      // Navigation is handled by useEffect when isAuthenticated changes
    } catch (err: unknown) {
      // Show error toast
      toast.error(
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'Login failed'
      );
    }
  };

  return (
    <div className="flex min-h-screen flex-col justify-center bg-strat-midnight py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="mb-8 text-center">
          <div className="flex justify-center">
            <StratEduLogo layout="lockup" appearance="dark" />
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-white">
            District evaluation platform
          </h1>
          <p className="mt-2 text-sm text-white/60">
            Danielson-aligned workflows for schools and districts
          </p>
        </div>
        <Card className="border-[var(--se-rule)] shadow-md">
          <CardHeader>
            <h2 className="text-lg font-semibold text-[var(--se-ink)]">
              Sign in
            </h2>
            <p className="mt-1 text-sm text-[var(--se-ink-muted)]">
              Use your district administrator or staff account
            </p>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >

              <div>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Email"
                      type="email"
                      {...field}
                      placeholder="Enter your email"
                    />
                  )}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Password"
                      type="password"
                      {...field}
                      placeholder="Enter your password"
                    />
                  )}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                loading={isSubmitting || isLoading}
                className="w-full"
              >
                Login
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="text-sm text-primary-800 hover:text-primary-900"
                >
                  Don't have an account? Register here
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
